"use client";

import { useMemo, useState } from "react";
import { Section } from "@astryxdesign/core/Section";
import { Center } from "@astryxdesign/core/Center";
import { VStack } from "@astryxdesign/core/VStack";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { LandingSectionBg } from "@/components/landing/landing-section-bg";
import { LandingCanvasText } from "@/components/landing/landing-canvas-text";
import { LANDING_PROSE_MAX, LANDING_SECTION_MAX } from "@/lib/landing/layout";
import { DEFAULT_NEWS } from "@/lib/landing/puck/defaults";
import {
  asLandingTextValue,
  landingTextProps,
  styledTextString,
  type LandingTextValue,
} from "@/lib/landing/puck/styled-text";

export type LandingNewsItem = {
  title: LandingTextValue;
  description: LandingTextValue;
  date: LandingTextValue;
  href: string;
  coverUrl?: string;
  /** Категория для фильтров (как на Tilda /news). */
  category?: LandingTextValue;
};

export type LandingNewsBodyProps = {
  sectionId?: string;
  title?: LandingTextValue;
  subtitle?: LandingTextValue;
  backgroundImageUrl?: string;
  backgroundColor?: string;
  layout?: "grid" | "stack";
  showFilters?: boolean;
  filterAllLabel?: LandingTextValue;
  items?: LandingNewsItem[];
};

const FALLBACK_COVERS = [
  "/brand/hero-bg-dark.png",
  "/brand/hero-bg-light.png",
  "/brand/logo-lab/logo-concept-prism-data.png",
];

function itemCover(item: LandingNewsItem, index: number): string {
  const cover = typeof item.coverUrl === "string" ? item.coverUrl.trim() : "";
  if (cover) return cover;
  return FALLBACK_COVERS[index % FALLBACK_COVERS.length]!;
}

function NewsCard({
  item,
  index,
  fieldIndex,
}: {
  item: LandingNewsItem;
  index: number;
  fieldIndex: number;
}) {
  const cover = itemCover(item, index);
  return (
    <a
      href={item.href || "#"}
      className="group flex min-w-0 flex-col overflow-hidden rounded-[1.25rem] border border-border bg-[var(--color-background-body)] no-underline shadow-[0_1px_2px_rgba(0,0,0,0.04)] transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:border-[color-mix(in_srgb,var(--landing-accent,#E11D48)_35%,var(--color-border))] hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)]"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden bg-[#121418]">
        {/* eslint-disable-next-line @next/next/no-img-element -- CMS / brand URLs */}
        <img
          src={cover}
          alt=""
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,transparent_45%,rgba(6,8,11,0.55)_100%)]"
        />
        <LandingCanvasText
          field={`items.${fieldIndex}.category`}
          value={asLandingTextValue(item.category)}
          label="Категория"
          inline
          hideIfEmpty
        >
          <span
            className="absolute left-3 top-3 rounded-full bg-[color-mix(in_srgb,var(--landing-accent,#E11D48)_92%,#06080B)] px-2.5 py-1 text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-white"
            {...landingTextProps(item.category)}
          />
        </LandingCanvasText>
      </div>

      <VStack gap={2} align="start" className="min-w-0 flex-1 p-5 sm:p-6">
        <LandingCanvasText
          field={`items.${fieldIndex}.date`}
          value={item.date}
          label="Дата"
          hideIfEmpty
        >
          <Text
            size="sm"
            color="secondary"
            className="text-[0.72rem] font-semibold uppercase tracking-[0.14em]"
            {...landingTextProps(item.date)}
          />
        </LandingCanvasText>
        <LandingCanvasText
          field={`items.${fieldIndex}.title`}
          value={item.title}
          label="Заголовок"
        >
          <Heading
            level={3}
            className="text-[1.05rem] font-bold leading-snug break-words [overflow-wrap:anywhere] transition-colors group-hover:text-[var(--landing-accent,#E11D48)] sm:text-[1.125rem]"
            {...landingTextProps(item.title)}
          />
        </LandingCanvasText>
        <LandingCanvasText
          field={`items.${fieldIndex}.description`}
          value={item.description}
          label="Описание"
          multiline
          hideIfEmpty
        >
          <Text
            color="secondary"
            className="line-clamp-3 break-words leading-relaxed [overflow-wrap:anywhere]"
            {...landingTextProps(item.description)}
          />
        </LandingCanvasText>
      </VStack>
    </a>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "shrink-0 rounded-full border border-[var(--landing-accent,#E11D48)] bg-[color-mix(in_srgb,var(--landing-accent,#E11D48)_12%,transparent)] px-4 py-2 text-sm font-medium text-[var(--landing-accent,#E11D48)]"
          : "shrink-0 rounded-full border border-border bg-[var(--color-background-body)] px-4 py-2 text-sm font-medium text-[var(--color-text-primary)] transition-colors hover:border-[color-mix(in_srgb,var(--landing-accent,#E11D48)_40%,var(--color-border))] hover:text-[var(--landing-accent,#E11D48)]"
      }
    >
      {label}
    </button>
  );
}

/** Лента новостей: заголовок + фильтры (брендбук) + карточки с обложкой. */
export function LandingNewsBody({
  sectionId = DEFAULT_NEWS.sectionId,
  title = DEFAULT_NEWS.title,
  subtitle = DEFAULT_NEWS.subtitle,
  backgroundImageUrl = DEFAULT_NEWS.backgroundImageUrl,
  backgroundColor,
  layout = DEFAULT_NEWS.layout,
  showFilters = DEFAULT_NEWS.showFilters,
  filterAllLabel = DEFAULT_NEWS.filterAllLabel,
  items = DEFAULT_NEWS.items,
}: LandingNewsBodyProps) {
  const list =
    Array.isArray(items) && items.length > 0 ? items : DEFAULT_NEWS.items;
  const allLabel = styledTextString(filterAllLabel).trim() || "Все";
  const categories = useMemo(() => {
    const set = new Set<string>();
    for (const item of list) {
      const c = styledTextString(item.category).trim();
      if (c) set.add(c);
    }
    return [...set];
  }, [list]);

  const [active, setActive] = useState(allLabel);
  const filtersOn =
    showFilters === true || showFilters === ("true" as unknown as boolean);
  const filtered =
    !filtersOn || active === allLabel
      ? list
      : list.filter((item) => styledTextString(item.category).trim() === active);

  const grid = layout !== "stack";

  return (
    <LandingSectionBg backgroundImageUrl={backgroundImageUrl} backgroundColor={backgroundColor}>
      <Section id={sectionId || undefined} variant="transparent" paddingBlock={10}>
        <Center>
          <VStack
            width="100%"
            maxWidth={LANDING_SECTION_MAX}
            gap={8}
            align="start"
            className="min-w-0 px-6"
          >
            <VStack gap={3} align="start" maxWidth={LANDING_PROSE_MAX} className="min-w-0">
              <LandingCanvasText field="title" value={title} label="Заголовок" multiline>
                <Heading
                  level={1}
                  type="display-3"
                  className="break-words"
                  {...landingTextProps(title)}
                />
              </LandingCanvasText>
              <LandingCanvasText
                field="subtitle"
                value={subtitle}
                label="Подзаголовок"
                multiline
                hideIfEmpty
              >
                <Text
                  type="large"
                  color="secondary"
                  className="break-words"
                  {...landingTextProps(subtitle)}
                />
              </LandingCanvasText>
            </VStack>

            {filtersOn && categories.length > 0 ? (
              <div className="flex w-full gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <FilterChip
                  label={allLabel}
                  active={active === allLabel}
                  onClick={() => setActive(allLabel)}
                />
                {categories.map((cat) => (
                  <FilterChip
                    key={cat}
                    label={cat}
                    active={active === cat}
                    onClick={() => setActive(cat)}
                  />
                ))}
              </div>
            ) : null}

            {filtered.length === 0 ? (
              <Text color="secondary">Нет новостей в этой категории.</Text>
            ) : (
              <div
                className={
                  grid
                    ? "grid w-full min-w-0 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3"
                    : "flex w-full min-w-0 flex-col gap-5"
                }
              >
                {filtered.map((item, index) => {
                  const fieldIndex = list.indexOf(item);
                  return (
                  <NewsCard
                    key={`${item.href}-${styledTextString(item.title)}-${index}`}
                    item={item}
                    index={index}
                    fieldIndex={fieldIndex >= 0 ? fieldIndex : index}
                  />
                  );
                })}
              </div>
            )}
          </VStack>
        </Center>
      </Section>
    </LandingSectionBg>
  );
}
